import { useT } from "@transifex/react";
import AchievementBar from "../../../basic/AchievementBar";
import { getAchievementColors, getAchievementTier } from "../../../constants/AchievementColors";
import { getLongDateFromMilliseconds } from "../../../helpers/DateTime";
import { formatScoreWithoutTrailingZero } from "../../../helpers/ScoringHelper";
import { getIsScoreShown } from "../../../helpers/StudentResultScoreReveal";
import { getReachedPercentage, type StudentResult } from "../../../helpers/StudentResultModel";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";

interface StudentResultSummaryProps {
    result: StudentResult;
}

/**
 * The at-a-glance box above the exercise list (the grade variant is E4).
 *
 * The percentage always shows — on an unscored test it is simply share-of-items-correct. The points
 * need a scored test *and* the reveal, or they would only restate the right/wrong marks; the
 * colors ride on the same gate, since a color is as much of a verdict as a number.
 */
const StudentResultSummary = (props: StudentResultSummaryProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t = useT();
    const t_result = t("Result", { _tags: "StudentResult, ResultSummary" });
    const t_points = t("Points", { _tags: "StudentResult, ResultSummary" });

    const percentage = getReachedPercentage(props.result.reachedScore, props.result.maxScore);
    const arePointsShown = getIsScoreShown(props.result.isScored);
    const colors = getAchievementColors(getAchievementTier(percentage, arePointsShown));

    const submittedDateLabel = props.result.submittedDate
        ? getLongDateFromMilliseconds(props.result.submittedDate)
        : "";
    const languageName = props.result.languageName;

    const headerLayout = getStyleBasedOnBreakpoint(Breakpoint.MD, "flex-row items-center", "flex-col items-stretch");
    // Above MD the title block spans the result box's height and pushes the two lines to its edges,
    // as the design has them; stacked, they simply sit together.
    const titleLayout = getStyleBasedOnBreakpoint(Breakpoint.MD, "self-stretch justify-between", "gap-xs");
    const resultBoxWidth = getStyleBasedOnBreakpoint(Breakpoint.MD, "min-w-[100px]", "w-full");

    const renderTestDetails = () => {
        if (!submittedDateLabel && !languageName) return null;

        // Top-aligned so a single-line language name stays level with the first line of a wrapped date.
        return (
            <div className="flex flex-row items-start gap-s text-l text-p6-grey-dark">
                {submittedDateLabel && <span>{submittedDateLabel}</span>}
                {submittedDateLabel && languageName && (
                    <span
                        className="w-px self-stretch bg-p6-grey-mid"
                        aria-hidden="true"
                    />
                )}
                {languageName && <span>{languageName}</span>}
            </div>
        );
    };

    const renderResultBox = () => (
        <div
            className={`flex flex-col items-center justify-center gap-xs rounded-card border-2 p-s ${colors.border} ${colors.boxFill} ${resultBoxWidth}`}
        >
            <span className="text-l text-p6-grey-mid">{t_result}</span>
            <span className="text-center text-xxl text-p6-grey-darkest">
                {`${formatScoreWithoutTrailingZero(percentage)}%`}
            </span>
        </div>
    );

    const renderPoints = () => {
        if (!arePointsShown) return null;

        const reached = formatScoreWithoutTrailingZero(props.result.reachedScore);
        const max = formatScoreWithoutTrailingZero(props.result.maxScore);

        return (
            <div className="flex flex-row items-center gap-xs text-l text-p6-grey-dark">
                <span className="font-semibold">{`${t_points}:`}</span>
                <span>{`${reached} / ${max}`}</span>
            </div>
        );
    };

    return (
        <section
            className="flex flex-col gap-m rounded-card border border-p6-grey-lightest bg-white p-m"
            data-testid="student-result-summary"
        >
            <div className={`flex gap-m ${headerLayout}`}>
                <div className={`flex min-w-[0] flex-1 flex-col ${titleLayout}`}>
                    {renderTestDetails()}
                    <h1 className="m-no text-xxl font-semibold break-words text-p6-grey-dark">
                        {props.result.testName || "—"}
                    </h1>
                </div>
                {renderResultBox()}
            </div>
            <div className="flex flex-col gap-xs">
                {renderPoints()}
                <AchievementBar
                    percentage={percentage}
                    isColored={arePointsShown}
                />
            </div>
        </section>
    );
};

export default StudentResultSummary;
